import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    PiPlusBold, PiPencilSimpleBold, PiTrashBold, PiXBold,
    PiFloppyDiskBold, PiArrowsClockwiseBold, PiWarningBold,
    PiImageBold, PiUsersBold, PiBuildingsBold,
    PiMagnifyingGlassBold, PiSquaresFourBold, PiListBold,
    PiHandshakeBold, PiLinkBold,
} from 'react-icons/pi';
import { PageHeader } from '@v2/design-system';
import TablePagination, { TABLE_PER_PAGE_OPTIONS } from '@v2/components/TablePagination';
import FileDropzone from '@v2/components/ui/FileDropzone';
import api from '@v2/lib/api';
import toast from 'react-hot-toast';
import { cn } from '@v2/utils/cn';

const FALLBACK = '/img/favicon.png';

/* ─── Types ──────────────────────────────────────────────────── */
type Franqueadora = {
    id: number;
    name: string;
    legal_name: string | null;
    cnpj: string | null;
    email: string | null;
    phone: string | null;
    city: string | null;
    state: string | null;
    logo_url: string | null;
    units_count: number;
    active: boolean;
    buckler_one_active?: boolean;
};

type NetworkUnit = {
    id: number;
    name: string;
    legal_name: string | null;
    cnpj: string | null;
    city: string | null;
    state: string | null;
};

type NetworkData = {
    franchisor: Franqueadora;
    units: NetworkUnit[];
    buckler_one: {
        id: number;
        active: boolean;
        contract_started_at: string;
    } | null;
    config_key: string | null;
    auto_link_patterns: string[];
};

type Meta = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
};

type FormState = {
    name: string; legal_name: string; cnpj: string; cnae: string;
    email: string; phone: string; address: string; neighborhood: string;
    zip_code: string; city: string; state: string; notes: string; active: boolean;
};

const EMPTY_FORM: FormState = {
    name: '', legal_name: '', cnpj: '', cnae: '',
    email: '', phone: '', address: '', neighborhood: '',
    zip_code: '', city: '', state: '', notes: '', active: true,
};

/* ─── Masks ─────────────────────────────────────────────────── */
const maskCnpj = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 14);
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, (_, a, b, c, e, f) =>
        `${a}.${b}.${c}/${e}` + (f ? `-${f}` : ''));
};
const maskPhone = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, (_, a, b, c) => `(${a}) ${b}` + (c ? `-${c}` : ''));
    return d.replace(/(\d{2})(\d{5})(\d{0,4})/, (_, a, b, c) => `(${a}) ${b}` + (c ? `-${c}` : ''));
};
const maskCep = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 8);
    return d.replace(/(\d{5})(\d{0,3})/, (_, a, b) => a + (b ? `-${b}` : ''));
};

const inputCls = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-200 dark:focus:border-amber-500/60';

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
    return (
        <div className={cn('flex flex-col gap-1', className)}>
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">{label}</label>
            {children}
        </div>
    );
}

/* ─── Logo Uploader ─────────────────────────────────────────── */
function LogoUploader({ clientId, currentUrl, onFileChange }: {
    clientId: number | null;
    currentUrl: string | null;
    onFileChange: (file: File | null) => void;
}) {
    const [preview, setPreview] = useState<string | null>(currentUrl);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        setPreview(currentUrl);
    }, [currentUrl]);

    async function handleFile(file: File) {
        if (!file.type.startsWith('image/') || file.size > 2 * 1024 * 1024) {
            toast.error('Use uma imagem PNG, JPG, WEBP ou SVG de até 2 MB.');
            return;
        }

        setUploading(true);
        const objectUrl = URL.createObjectURL(file);
        setPreview(objectUrl);

        if (clientId) {
            const form = new FormData();
            form.append('logo', file);
            try {
                const { data } = await api.post(`/clients/${clientId}/logo`, form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                setPreview(data.logo_url ?? objectUrl);
                onFileChange(null);
                toast.success('Logo atualizado');
            } catch {
                setPreview(currentUrl);
                onFileChange(null);
                toast.error('Falha ao enviar o logo. Verifique o formato e tente novamente.');
            }
        } else {
            onFileChange(file);
        }
        setUploading(false);
    }

    async function removeLogo() {
        if (clientId) {
            try {
                await api.delete(`/clients/${clientId}/logo`);
                toast.success('Logo removido');
            } catch {
                toast.error('Não foi possível remover o logo');
                return;
            }
        }
        setPreview(null);
        onFileChange(null);
    }

    return (
        <div className="flex items-start gap-4">
            <div className="relative flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800">
                {preview ? (
                    <img src={preview} alt="Logo" className="size-full object-contain p-1" />
                ) : (
                    <PiImageBold className="size-8 text-gray-300 dark:text-zinc-700" />
                )}
                {uploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-zinc-900/70">
                        <PiArrowsClockwiseBold className="size-5 animate-spin text-amber-500" />
                    </div>
                )}
            </div>

            <div className="min-w-0 flex-1 space-y-2">
                <FileDropzone
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    maxSizeMb={2}
                    loading={uploading}
                    disabled={uploading}
                    label={preview ? 'Trocar logo' : 'Arraste o logo aqui'}
                    hint="PNG, JPG, SVG ou WebP · clique para selecionar"
                    showSelected={false}
                    className="[&>label]:py-4"
                    onFiles={(files) => {
                        if (files[0]) void handleFile(files[0]);
                    }}
                    onError={(msg) => toast.error(msg)}
                />
                {preview && (
                    <button
                        type="button"
                        onClick={() => void removeLogo()}
                        className="flex items-center gap-2 rounded-xl border border-red-200 px-3 py-1.5 text-xs font-bold text-red-500 transition hover:bg-red-50 dark:border-red-500/30 dark:hover:bg-red-500/10"
                    >
                        <PiTrashBold className="size-3.5" />
                        Remover
                    </button>
                )}
            </div>
        </div>
    );
}

/* ─── Form Modal (Create / Edit) ────────────────────────────── */
function FranqueadoraFormModal({ editing, onClose, onSaved }: {
    editing: Franqueadora | null;
    onClose: () => void;
    onSaved: () => void;
}) {
    const backdropRef = useRef<HTMLDivElement>(null);
    const queryClient = useQueryClient();
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (editing) {
            setForm({
                name: editing.name ?? '',
                legal_name: editing.legal_name ?? '',
                cnpj: maskCnpj(editing.cnpj ?? ''),
                cnae: '',
                email: editing.email ?? '',
                phone: maskPhone(editing.phone ?? ''),
                address: '', neighborhood: '', zip_code: '',
                city: editing.city ?? '', state: editing.state ?? '',
                notes: '', active: editing.active,
            });
        } else {
            setForm(EMPTY_FORM);
        }
    }, [editing]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    const set = (key: keyof FormState, v: string | boolean) => {
        setForm((f) => ({ ...f, [key]: v }));
        setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
    };

    const { mutate, isPending } = useMutation({
        mutationFn: async () => {
            if (editing) {
                const { data } = await api.put(`/clients/${editing.id}`, form);
                if (logoFile) {
                    const fd = new FormData();
                    fd.append('logo', logoFile);
                    await api.post(`/clients/${editing.id}/logo`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                }
                return data;
            }

            const { data } = await api.post('/clients', form);
            if (logoFile && data.data?.id) {
                const fd = new FormData();
                fd.append('logo', logoFile);
                await api.post(`/clients/${data.data.id}/logo`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['franchisors'] });
            queryClient.invalidateQueries({ queryKey: ['clients-stats'] });
            setLogoFile(null);
            toast.success(editing ? 'Franqueadora atualizada' : 'Franqueadora criada');
            onSaved();
            onClose();
        },
        onError: (err: unknown) => {
            const resp = (err as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }).response?.data;
            if (resp?.errors) {
                const flat: Record<string, string> = {};
                for (const [k, v] of Object.entries(resp.errors)) flat[k] = v[0];
                setErrors(flat);
            }
            toast.error(resp?.message ?? 'Não foi possível salvar a franqueadora');
        },
    });

    return (
        <div
            ref={backdropRef}
            onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
            className="fixed inset-0 z-50 flex items-start justify-end bg-black/60 backdrop-blur-sm"
        >
            <div className="flex h-full w-full max-w-xl flex-col overflow-hidden border-l border-gray-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">

                {/* Header */}
                <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-zinc-800">
                    <div>
                        <h2 className="text-base font-black text-gray-900 dark:text-white">
                            {editing ? 'Editar franqueadora' : 'Nova franqueadora'}
                        </h2>
                        <p className="text-xs text-gray-400 dark:text-zinc-600">
                            {editing ? `Editando: ${editing.name}` : 'Cadastrar nova rede ou grupo'}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className="flex size-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition hover:bg-gray-100 dark:border-zinc-700 dark:hover:bg-zinc-800">
                        <PiXBold className="size-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    <div className="flex flex-col gap-5">

                        {/* Logo */}
                        <section>
                            <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Logo</p>
                            <LogoUploader
                                clientId={editing?.id ?? null}
                                currentUrl={editing?.logo_url ?? null}
                                onFileChange={setLogoFile}
                            />
                        </section>

                        {/* Identificação */}
                        <section>
                            <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Identificação</p>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <Field label="Nome fantasia *" className="sm:col-span-2">
                                    <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} />
                                    {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                                </Field>
                                <Field label="Razão social" className="sm:col-span-2">
                                    <input className={inputCls} value={form.legal_name} onChange={(e) => set('legal_name', e.target.value)} />
                                </Field>
                                <Field label="CNPJ">
                                    <input className={cn(inputCls, 'font-mono')} inputMode="numeric" value={form.cnpj} onChange={(e) => set('cnpj', maskCnpj(e.target.value))} placeholder="00.000.000/0000-00" />
                                </Field>
                                <Field label="Telefone">
                                    <input className={cn(inputCls, 'font-mono')} inputMode="numeric" value={form.phone} onChange={(e) => set('phone', maskPhone(e.target.value))} placeholder="(00) 00000-0000" />
                                </Field>
                                <Field label="E-mail" className="sm:col-span-2">
                                    <input className={inputCls} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
                                    {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                                </Field>
                            </div>
                        </section>

                        {/* Endereço */}
                        <section>
                            <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Endereço</p>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <Field label="Cidade">
                                    <input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} />
                                </Field>
                                <Field label="UF">
                                    <input className={cn(inputCls, 'uppercase')} maxLength={2} value={form.state} onChange={(e) => set('state', e.target.value.toUpperCase())} placeholder="SP" />
                                </Field>
                                <Field label="CEP">
                                    <input className={cn(inputCls, 'font-mono')} inputMode="numeric" value={form.zip_code} onChange={(e) => set('zip_code', maskCep(e.target.value))} placeholder="00000-000" />
                                </Field>
                                <Field label="Bairro">
                                    <input className={inputCls} value={form.neighborhood} onChange={(e) => set('neighborhood', e.target.value)} />
                                </Field>
                                <Field label="Logradouro" className="sm:col-span-2">
                                    <input className={inputCls} value={form.address} onChange={(e) => set('address', e.target.value)} />
                                </Field>
                            </div>
                        </section>

                        {/* Status */}
                        <section>
                            <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Status</p>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => set('active', !form.active)}
                                    className={cn('relative shrink-0 rounded-full transition-colors duration-200', form.active ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-700')}
                                    style={{ width: 44, height: 24 }}
                                >
                                    <span className="absolute top-[2px] size-5 rounded-full bg-white shadow-md transition-all duration-200" style={{ left: form.active ? 22 : 2 }} />
                                </button>
                                <span className="text-sm text-gray-600 dark:text-zinc-400">{form.active ? 'Ativa' : 'Inativa'}</span>
                            </div>
                        </section>

                        {/* Observações */}
                        <section>
                            <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Observações</p>
                            <textarea className={cn(inputCls, 'min-h-[70px] resize-y')} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Notas internas…" />
                        </section>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-100 px-6 py-4 dark:border-zinc-800">
                    <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-500 transition hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800">
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={() => mutate()}
                        disabled={isPending}
                        className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2 text-sm font-black text-white transition hover:bg-amber-600 disabled:opacity-60 dark:bg-[#C9A84C] dark:text-zinc-900"
                    >
                        {isPending ? <PiArrowsClockwiseBold className="size-4 animate-spin" /> : <PiFloppyDiskBold className="size-4" />}
                        {isPending ? 'Salvando…' : 'Salvar'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Network Modal (unidades + Buckler One) ─────────────────── */
function FranqueadoraNetworkModal({ franchisor, onClose }: {
    franchisor: Franqueadora;
    onClose: () => void;
}) {
    const backdropRef = useRef<HTMLDivElement>(null);
    const queryClient = useQueryClient();
    const [candidateSearch, setCandidateSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const { data: network, isLoading, refetch } = useQuery({
        queryKey: ['franchisor-network', franchisor.id],
        queryFn: async () => {
            const { data } = await api.get(`/clients/${franchisor.id}/franchisor-network`);
            return data.data as NetworkData;
        },
    });

    const { data: candidates = [], isFetching: loadingCandidates } = useQuery({
        queryKey: ['franchisor-candidates', franchisor.id, candidateSearch],
        queryFn: async () => {
            const params: Record<string, string> = {};
            if (candidateSearch) params.search = candidateSearch;
            const { data } = await api.get(`/clients/${franchisor.id}/franchisor-network/candidates`, { params });
            return data.data as NetworkUnit[];
        },
        enabled: !!network,
    });

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['franchisors'] });
        queryClient.invalidateQueries({ queryKey: ['franchisor-network', franchisor.id] });
        queryClient.invalidateQueries({ queryKey: ['franchisor-candidates', franchisor.id] });
    };

    const { mutate: autoLink, isPending: autoLinking } = useMutation({
        mutationFn: () => api.post(`/clients/${franchisor.id}/franchisor-network/link`, { auto: true }),
        onSuccess: (res) => {
            toast.success(res.data.message ?? 'Unidades vinculadas');
            setSelectedIds([]);
            invalidate();
            refetch();
        },
        onError: () => toast.error('Falha ao vincular unidades automaticamente'),
    });

    const { mutate: linkSelected, isPending: linking } = useMutation({
        mutationFn: (ids: number[]) => api.post(`/clients/${franchisor.id}/franchisor-network/link`, { client_ids: ids }),
        onSuccess: (res) => {
            toast.success(res.data.message ?? 'Unidades vinculadas');
            setSelectedIds([]);
            invalidate();
            refetch();
        },
        onError: () => toast.error('Falha ao vincular unidades'),
    });

    const { mutate: unlinkUnit, isPending: unlinking } = useMutation({
        mutationFn: (id: number) => api.post(`/clients/${franchisor.id}/franchisor-network/unlink`, { client_ids: [id] }),
        onSuccess: () => {
            toast.success('Unidade desvinculada');
            invalidate();
            refetch();
        },
    });

    const { mutate: enableBucklerOne, isPending: enablingBo } = useMutation({
        mutationFn: () => api.post(`/clients/${franchisor.id}/franchisor-network/buckler-one`, {}),
        onSuccess: () => {
            toast.success('Buckler One habilitado para esta rede');
            invalidate();
            refetch();
        },
        onError: () => toast.error('Falha ao habilitar Buckler One'),
    });

    const toggleCandidate = (id: number) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    const units = network?.units ?? [];
    const bucklerOne = network?.buckler_one;
    const patterns = network?.auto_link_patterns ?? [];

    return (
        <div
            ref={backdropRef}
            onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
            className="fixed inset-0 z-50 flex items-start justify-end bg-black/60 backdrop-blur-sm"
        >
            <div className="flex h-full w-full max-w-2xl flex-col overflow-hidden border-l border-gray-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">
                <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-zinc-800">
                    <div>
                        <h2 className="text-base font-black text-gray-900 dark:text-white">Rede — {franchisor.name}</h2>
                        <p className="text-xs text-gray-400 dark:text-zinc-600">Unidades vinculadas e parceria Buckler One</p>
                    </div>
                    <button type="button" onClick={onClose} className="flex size-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition hover:bg-gray-100 dark:border-zinc-700 dark:hover:bg-zinc-800">
                        <PiXBold className="size-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                    {/* Buckler One */}
                    <section className="rounded-2xl border border-amber-200/60 bg-amber-50/50 p-4 dark:border-amber-500/20 dark:bg-amber-500/5">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="flex items-center gap-2 text-sm font-black text-amber-800 dark:text-amber-300">
                                    <PiHandshakeBold className="size-4" /> Buckler One
                                </p>
                                {bucklerOne?.active ? (
                                    <p className="mt-1 text-xs text-amber-700/80 dark:text-amber-400/80">
                                        Ativo desde {new Date(bucklerOne.contract_started_at + 'T12:00:00').toLocaleDateString('pt-BR')}
                                    </p>
                                ) : (
                                    <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">
                                        Parceria não configurada — deals da rede não entram no programa.
                                    </p>
                                )}
                            </div>
                            {!bucklerOne?.active && (
                                <button
                                    type="button"
                                    disabled={enablingBo}
                                    onClick={() => enableBucklerOne()}
                                    className="shrink-0 rounded-xl bg-amber-500 px-3 py-1.5 text-xs font-black text-white hover:bg-amber-600 disabled:opacity-60 dark:bg-[#C9A84C] dark:text-zinc-900"
                                >
                                    {enablingBo ? 'Habilitando…' : 'Habilitar'}
                                </button>
                            )}
                        </div>
                    </section>

                    {/* Unidades vinculadas */}
                    <section>
                        <div className="mb-3 flex items-center justify-between">
                            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">
                                Unidades vinculadas ({units.length})
                            </p>
                            {patterns.length > 0 && (
                                <button
                                    type="button"
                                    disabled={autoLinking}
                                    onClick={() => autoLink()}
                                    className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700 transition hover:bg-amber-100 disabled:opacity-60 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400"
                                >
                                    <PiLinkBold className="size-3" />
                                    {autoLinking ? 'Vinculando…' : 'Vincular todas automaticamente'}
                                </button>
                            )}
                        </div>
                        {isLoading ? (
                            <div className="h-20 animate-pulse rounded-xl bg-gray-100 dark:bg-zinc-800" />
                        ) : units.length === 0 ? (
                            <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-400 dark:border-zinc-700 dark:text-zinc-600">
                                Nenhuma unidade vinculada ainda.
                            </p>
                        ) : (
                            <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-zinc-800 dark:border-zinc-800">
                                {units.map((u) => (
                                    <li key={u.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold text-gray-800 dark:text-zinc-200">{u.name}</p>
                                            <p className="truncate text-xs text-gray-400 dark:text-zinc-600">
                                                {[u.city, u.state].filter(Boolean).join(' / ') || u.cnpj || '—'}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={unlinking}
                                            onClick={() => unlinkUnit(u.id)}
                                            className="shrink-0 text-[11px] font-bold text-red-500 hover:text-red-600"
                                        >
                                            Desvincular
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* Candidatos */}
                    <section>
                        <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">
                            Adicionar unidades
                        </p>
                        <div className="mb-3 flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900">
                            <PiMagnifyingGlassBold className="size-4 shrink-0 text-gray-400" />
                            <input
                                className="flex-1 bg-transparent text-sm outline-none dark:text-zinc-200"
                                placeholder="Buscar cliente para vincular…"
                                value={candidateSearch}
                                onChange={(e) => setCandidateSearch(e.target.value)}
                            />
                        </div>
                        {loadingCandidates ? (
                            <div className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-zinc-800" />
                        ) : candidates.length === 0 ? (
                            <p className="text-xs text-gray-400 dark:text-zinc-600">Nenhum candidato encontrado.</p>
                        ) : (
                            <ul className="max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-xl border border-gray-200 dark:divide-zinc-800 dark:border-zinc-800">
                                {candidates.map((c) => (
                                    <li key={c.id}>
                                        <label className="flex cursor-pointer items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-zinc-800/50">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(c.id)}
                                                onChange={() => toggleCandidate(c.id)}
                                                className="rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                                            />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium text-gray-800 dark:text-zinc-200">{c.name}</span>
                                                <span className="block truncate text-xs text-gray-400">{c.cnpj ?? [c.city, c.state].filter(Boolean).join(' / ')}</span>
                                            </span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {selectedIds.length > 0 && (
                            <button
                                type="button"
                                disabled={linking}
                                onClick={() => linkSelected(selectedIds)}
                                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-2.5 text-sm font-black text-white hover:bg-amber-600 disabled:opacity-60 dark:bg-[#C9A84C] dark:text-zinc-900"
                            >
                                <PiLinkBold className="size-4" />
                                Vincular {selectedIds.length} selecionada(s)
                            </button>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}

/* ─── Delete Confirm ────────────────────────────────────────── */
function DeleteConfirm({ target, onClose, onDeleted }: {
    target: Franqueadora;
    onClose: () => void;
    onDeleted: () => void;
}) {
    const queryClient = useQueryClient();
    const { mutate, isPending } = useMutation({
        mutationFn: () => api.delete(`/clients/${target.id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['franchisors'] });
            queryClient.invalidateQueries({ queryKey: ['clients-stats'] });
            onDeleted();
            onClose();
        },
    });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">
                <div className="mb-4 flex size-12 items-center justify-center rounded-xl bg-red-50 dark:bg-red-500/10">
                    <PiWarningBold className="size-6 text-red-500" />
                </div>
                <h3 className="mb-1 text-base font-black text-gray-900 dark:text-white">Excluir franqueadora</h3>
                <p className="mb-1 text-sm text-gray-500 dark:text-zinc-400">
                    Tem certeza que deseja excluir <strong>{target.name}</strong>?
                </p>
                {(target.units_count ?? 0) > 0 && (
                    <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                        ⚠ Esta franqueadora tem <strong>{target.units_count}</strong> unidade(s) vinculada(s). Elas serão desvinculadas mas não excluídas.
                    </p>
                )}
                <div className="flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800">
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={() => mutate()}
                        disabled={isPending}
                        className="flex items-center gap-2 rounded-xl bg-red-500 px-4 py-2 text-sm font-black text-white hover:bg-red-600 disabled:opacity-60"
                    >
                        {isPending ? <PiArrowsClockwiseBold className="size-4 animate-spin" /> : <PiTrashBold className="size-4" />}
                        {isPending ? 'Excluindo…' : 'Excluir'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Card View ─────────────────────────────────────────────── */
function CardView({ rows, onEdit, onDelete, onManage }: {
    rows: Franqueadora[];
    onEdit: (f: Franqueadora) => void;
    onDelete: (f: Franqueadora) => void;
    onManage: (f: Franqueadora) => void;
}) {
    return (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((f) => (
                <div key={f.id} className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-zinc-700">
                    <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 to-yellow-300" />
                    <div className="flex flex-1 gap-4 p-5">
                        <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-100 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-800">
                            <img src={f.logo_url || FALLBACK} alt={f.name} className="size-full object-contain p-1.5" onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <h3 className="truncate font-black text-gray-900 dark:text-white">{f.name}</h3>
                                    {f.legal_name && <p className="truncate text-xs text-gray-400 dark:text-zinc-500">{f.legal_name}</p>}
                                </div>
                                {!f.active && (
                                    <span className="shrink-0 rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[10px] font-black text-gray-400 dark:border-zinc-700 dark:bg-zinc-800">Inativa</span>
                                )}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                                {f.cnpj && <span className="font-mono text-[11px] text-gray-400 dark:text-zinc-600">{f.cnpj}</span>}
                                {(f.city || f.state) && <span className="text-[11px] text-gray-400 dark:text-zinc-600">{[f.city, f.state].filter(Boolean).join(' / ')}</span>}
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-500">
                                    <PiUsersBold className="size-3.5 text-amber-500" />
                                    <span className="font-bold">{f.units_count ?? 0}</span>
                                    <span>unidade(s)</span>
                                </div>
                                {f.buckler_one_active && (
                                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/60 bg-amber-50 px-2 py-0.5 text-[10px] font-black text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
                                        <PiHandshakeBold className="size-3" /> Buckler One
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex border-t border-gray-100 dark:border-zinc-800">
                        <button type="button" onClick={() => onManage(f)} className="flex flex-1 items-center justify-center gap-2 py-2.5 text-xs font-bold text-gray-500 transition hover:bg-amber-50 hover:text-amber-600 dark:text-zinc-500 dark:hover:bg-amber-500/10 dark:hover:text-amber-400">
                            <PiUsersBold className="size-3.5" />Rede
                        </button>
                        <div className="w-px bg-gray-100 dark:bg-zinc-800" />
                        <button type="button" onClick={() => onEdit(f)} className="flex flex-1 items-center justify-center gap-2 py-2.5 text-xs font-bold text-gray-500 transition hover:bg-gray-50 hover:text-amber-600 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-amber-400">
                            <PiPencilSimpleBold className="size-3.5" />Editar
                        </button>
                        <div className="w-px bg-gray-100 dark:bg-zinc-800" />
                        <button type="button" onClick={() => onDelete(f)} className="flex flex-1 items-center justify-center gap-2 py-2.5 text-xs font-bold text-gray-500 transition hover:bg-red-50 hover:text-red-500 dark:text-zinc-500 dark:hover:bg-red-500/10 dark:hover:text-red-400">
                            <PiTrashBold className="size-3.5" />Excluir
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ─── List View ─────────────────────────────────────────────── */
function ListView({ rows, onEdit, onDelete, onManage }: {
    rows: Franqueadora[];
    onEdit: (f: Franqueadora) => void;
    onDelete: (f: Franqueadora) => void;
    onManage: (f: Franqueadora) => void;
}) {
    return (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
            <table className="min-w-full text-sm">
                <thead className="border-b border-gray-100 dark:border-zinc-800">
                    <tr className="text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-zinc-600">
                        <th className="px-4 py-3 text-left" style={{ width: '3rem' }}></th>
                        <th className="px-4 py-3 text-left">Nome</th>
                        <th className="px-4 py-3 text-left">CNPJ</th>
                        <th className="px-4 py-3 text-left">Localização</th>
                        <th className="px-4 py-3 text-center">Unidades</th>
                        <th className="px-4 py-3 text-center">Buckler One</th>
                        <th className="px-4 py-3 text-center">Status</th>
                        <th className="px-4 py-3"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800/60">
                    {rows.map((f) => (
                        <tr key={f.id} className="transition hover:bg-gray-50/60 dark:hover:bg-zinc-800/40">
                            <td className="px-4 py-3">
                                <div className="flex size-9 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-800">
                                    <img src={f.logo_url || FALLBACK} alt="" className="size-full object-contain p-1" onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }} />
                                </div>
                            </td>
                            <td className="px-4 py-3">
                                <p className="font-semibold text-gray-800 dark:text-zinc-100">{f.name}</p>
                                {f.legal_name && <p className="text-xs text-gray-400 dark:text-zinc-600">{f.legal_name}</p>}
                            </td>
                            <td className="px-4 py-3">
                                <span className="font-mono text-xs text-gray-500 dark:text-zinc-400">{f.cnpj ?? '—'}</span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500 dark:text-zinc-400">
                                {[f.city, f.state].filter(Boolean).join(' / ') || '—'}
                            </td>
                            <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-black text-amber-600 dark:bg-amber-500/10 dark:text-amber-400">
                                    <PiUsersBold className="size-3" />{f.units_count ?? 0}
                                </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                                {f.buckler_one_active
                                    ? <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/60 bg-amber-50 px-2 py-0.5 text-[11px] font-black text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400"><PiHandshakeBold className="size-3" />Ativo</span>
                                    : <span className="text-xs text-gray-300 dark:text-zinc-700">—</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                                {f.active
                                    ? <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-black text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">Ativa</span>
                                    : <span className="rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-black text-gray-400 dark:border-zinc-700 dark:bg-zinc-800">Inativa</span>
                                }
                            </td>
                            <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                    <button type="button" onClick={() => onManage(f)} title="Gerenciar rede" className="flex size-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-600 dark:border-zinc-700 dark:text-zinc-500 dark:hover:border-amber-500/30 dark:hover:bg-amber-500/10 dark:hover:text-amber-400">
                                        <PiUsersBold className="size-3.5" />
                                    </button>
                                    <button type="button" onClick={() => onEdit(f)} className="flex size-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-600 dark:border-zinc-700 dark:text-zinc-500 dark:hover:border-amber-500/30 dark:hover:bg-amber-500/10 dark:hover:text-amber-400">
                                        <PiPencilSimpleBold className="size-3.5" />
                                    </button>
                                    <button type="button" onClick={() => onDelete(f)} className="flex size-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition hover:border-red-300 hover:bg-red-50 hover:text-red-500 dark:border-zinc-700 dark:text-zinc-500 dark:hover:border-red-500/30 dark:hover:bg-red-500/10 dark:hover:text-red-400">
                                        <PiTrashBold className="size-3.5" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ─── Main Page ──────────────────────────────────────────────── */
type ViewMode = 'card' | 'list';

export default function FranqueadoraListPage() {
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState<ViewMode>('card');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [deleting, setDeleting] = useState<Franqueadora | null>(null);
    const [managing, setManaging] = useState<Franqueadora | null>(null);
    const navigate = useNavigate();

    // Reset página ao mudar busca/per_page
    useEffect(() => { setPage(1); }, [search, perPage]);

    const { data, isLoading } = useQuery({
        queryKey: ['franchisors', search, page, perPage],
        queryFn: async () => {
            const params: Record<string, string> = {
                page: String(page),
                per_page: String(perPage),
                roots_only: '1',
                with_buckler_one: '1',
            };
            if (search) params.search = search;
            const { data: body } = await api.get('/clients', { params });
            return body as { data: Franqueadora[]; meta: Meta };
        },
        placeholderData: (prev) => prev,
        staleTime: 30_000,
    });

    const rows = data?.data ?? [];
    const meta = data?.meta;

    function openEdit(f: Franqueadora) { navigate(`/franchisors/${f.id}/edit`); }
    function openDelete(f: Franqueadora) { setDeleting(f); }
    function openManage(f: Franqueadora) { setManaging(f); }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Franqueadoras"
                breadcrumb={[{ name: 'Cadastros' }, { name: 'Franqueadoras' }]}
            >
                <button
                    type="button"
                    onClick={() => navigate('/franchisors/new')}
                    className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-black text-white transition hover:bg-amber-600 dark:bg-[#C9A84C] dark:text-zinc-900"
                >
                    <PiPlusBold className="size-4" />
                    Nova franqueadora
                </button>
            </PageHeader>

            {/* Toolbar: busca + visão + per_page */}
            <div className="flex flex-wrap items-center gap-3">
                {/* Busca */}
                <div className="flex flex-1 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 dark:border-zinc-800 dark:bg-zinc-900">
                    <PiMagnifyingGlassBold className="size-4 shrink-0 text-gray-400" />
                    <input
                        className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400 dark:text-zinc-200"
                        placeholder="Buscar por nome, razão social ou CNPJ…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button type="button" onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
                            <PiXBold className="size-3.5" />
                        </button>
                    )}
                </div>

                {/* Toggle visão */}
                <div className="flex overflow-hidden rounded-xl border border-gray-200 dark:border-zinc-700">
                    {(['card', 'list'] as ViewMode[]).map((mode) => (
                        <button
                            key={mode}
                            type="button"
                            onClick={() => setViewMode(mode)}
                            title={mode === 'card' ? 'Visão em cards' : 'Visão em lista'}
                            className={cn(
                                'flex size-9 items-center justify-center transition',
                                viewMode === mode
                                    ? 'bg-amber-500 text-white dark:bg-[#C9A84C] dark:text-zinc-900'
                                    : 'bg-white text-gray-400 hover:bg-gray-50 dark:bg-zinc-900 dark:text-zinc-500 dark:hover:bg-zinc-800',
                            )}
                        >
                            {mode === 'card' ? <PiSquaresFourBold className="size-4" /> : <PiListBold className="size-4" />}
                        </button>
                    ))}
                </div>
            </div>

            {/* Conteúdo */}
            {isLoading ? (
                <div className={viewMode === 'card' ? 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3' : 'space-y-2'}>
                    {Array.from({ length: perPage }).map((_, i) => (
                        <div key={i} className="h-14 animate-pulse rounded-xl bg-gray-100 dark:bg-zinc-800" />
                    ))}
                </div>
            ) : rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-zinc-600">
                    <PiBuildingsBold className="mb-3 size-12 opacity-30" />
                    <p className="font-bold">Nenhuma franqueadora encontrada</p>
                    <p className="text-sm">Crie a primeira usando o botão acima</p>
                </div>
            ) : viewMode === 'card' ? (
                <CardView rows={rows} onEdit={openEdit} onDelete={openDelete} onManage={openManage} />
            ) : (
                <ListView rows={rows} onEdit={openEdit} onDelete={openDelete} onManage={openManage} />
            )}

            {meta && (
                <TablePagination
                    meta={{
                        current_page: meta.current_page,
                        last_page: meta.last_page,
                        per_page: meta.per_page,
                        total: meta.total,
                        from: meta.from,
                        to: meta.to,
                    }}
                    onPageChange={setPage}
                    onPerPageChange={(value) => {
                        setPerPage(value);
                        setPage(1);
                    }}
                    perPageOptions={[...TABLE_PER_PAGE_OPTIONS]}
                    itemLabel="franqueadora"
                />
            )}

            {managing && (
                <FranqueadoraNetworkModal
                    franchisor={managing}
                    onClose={() => setManaging(null)}
                />
            )}

            {deleting && (
                <DeleteConfirm
                    target={deleting}
                    onClose={() => setDeleting(null)}
                    onDeleted={() => setDeleting(null)}
                />
            )}
        </div>
    );
}
